import { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { StepLayout } from "../components/StepLayout";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  getMerchantStepPath,
  PROVIDER_TYPE,
  getResumeStep,
} from "../constants";
import { useApplicationId } from "../hooks/use-application-id";
import { useAuth } from "@/providers/auth";
import { useApplicationInfo } from "@/queries/provider-onboarding/use-application-info";
import { APPLICATION_STATUS } from "@/queries/provider-onboarding/application-status";
import { useCompleteIdv } from "@/queries/provider-onboarding/use-idv";
import { IdvQuestion } from "@/queries/provider-onboarding/types";
import { toast } from "react-toastify";

const IDV_STATUSES = new Set([
  APPLICATION_STATUS.APPLICATION_CREATED,
  APPLICATION_STATUS.IDENTITY_VERIFICATION,
  APPLICATION_STATUS.IDENTITY_VERIFICATION_SUBMITTED,
]);

interface LocationState {
  sessionId: string;
  questions: IdvQuestion[];
  otp: boolean;
}

const Step3 = () => {
  const history = useHistory();
  const location = useLocation<LocationState>();
  const applicationId = useApplicationId();
  const { cognitoId, clientId, userRole } = useAuth();
  const userId =
    userRole === "admin" || userRole === "auth_user" ? clientId : cognitoId;

  const { data: applicationInfo, isLoading: isCheckingStatus } =
    useApplicationInfo(userId!, PROVIDER_TYPE);

  useEffect(() => {
    if (isCheckingStatus || !applicationInfo) return;
    if (!applicationInfo.external_application_id) {
      history.replace(getMerchantStepPath(1));
      return;
    }
    if (!IDV_STATUSES.has(applicationInfo.status as any)) {
      history.replace(
        getResumeStep(
          applicationInfo.status,
          applicationInfo.external_application_id,
        ),
      );
    }
  }, [applicationInfo, isCheckingStatus, history]);

  const state = location.state as LocationState | undefined;
  const sessionId = state?.sessionId ?? "";
  const questions = state?.questions ?? [];
  const isOtp = state?.otp ?? true;

  const [otp, setOtp] = useState("");

  const [kbaAnswers, setKbaAnswers] = useState<Record<number, string>>({});

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mutate: completeIdv } = useCompleteIdv({
    onSuccess: () => {
      history.push(getMerchantStepPath(4, applicationId));
    },
    onError: () => {
      setIsSubmitting(false);
      toast.error(
        "Identity verification failed. Please check your answer and try again.",
      );
    },
  });

  const handleSubmit = () => {
    if (!sessionId) return;
    setIsSubmitting(true);

    if (isOtp) {
      completeIdv({
        userId: userId || "",
        applicationId,
        providerType: PROVIDER_TYPE,
        payload: {
          sessionId,
          answers: [{ questionId: 100, answer: otp }],
        },
      });
    } else {
      const answers = questions.map((q) => ({
        questionId: q.id,
        answer: kbaAnswers[q.id] ?? "",
      }));
      completeIdv({
        userId: userId || "",
        applicationId,
        providerType: PROVIDER_TYPE,
        payload: { sessionId, answers },
      });
    }
  };

  const otpReady = isOtp && otp.length >= 4;
  const kbaReady = !isOtp && questions.every((q) => !!kbaAnswers[q.id]);
  const canSubmit = (otpReady || kbaReady) && !isSubmitting;

  return (
    <StepLayout
      step={3}
      title="Identity Verification"
      onNext={handleSubmit}
      nextLabel={isSubmitting ? "Submitting..." : "Submit"}
      nextDisabled={!canSubmit}
      hideBack
    >
      {isOtp ? (
        <>
          <p className="text-sm text-gray-600 leading-relaxed -mt-2">
            Enter the 6-digit PIN you received via text message. Do not share
            this code with anyone.
          </p>
          <div className="space-y-2">
            <Label>Enter your PIN</Label>
            <Input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              disabled={isSubmitting}
              className="tracking-widest text-center text-lg font-semibold w-40"
            />
          </div>
          <p className="text-sm text-gray-500">
            Text messages can take up to 2 minutes to arrive. Didn't receive it?{" "}
            <button
              type="button"
              onClick={() =>
                history.push(getMerchantStepPath(4, applicationId))
              }
              className="underline text-gray-800 hover:text-gray-900 font-medium"
            >
              Click here to continue.
            </button>
          </p>
        </>
      ) : (
        <>
          <p className="text-sm text-gray-600 leading-relaxed -mt-2">
            Please answer the following questions to verify your identity.
          </p>
          <div className="space-y-6">
            {questions.map((q) => (
              <div key={q.id} className="space-y-2">
                <Label>{q.text}</Label>
                <div className="space-y-2">
                  {q.choices?.map((choice) => (
                    <label
                      key={choice}
                      className="flex items-center gap-3 cursor-pointer"
                    >
                      <input
                        type="radio"
                        name={`question-${q.id}`}
                        value={choice}
                        checked={kbaAnswers[q.id] === choice}
                        onChange={() =>
                          setKbaAnswers((prev) => ({ ...prev, [q.id]: choice }))
                        }
                        disabled={isSubmitting}
                        className="accent-skin-primary"
                      />
                      <span className="text-sm text-gray-700">{choice}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-400">
            Having trouble?{" "}
            <button
              type="button"
              onClick={() =>
                history.push(getMerchantStepPath(4, applicationId))
              }
              className="underline hover:text-gray-600"
            >
              Skip this step.
            </button>
          </p>
        </>
      )}
    </StepLayout>
  );
};

export default Step3;
